/**
 * Deteksi error "aset build basi" (stale build assets).
 *
 * Terjadi ketika halaman yang sedang terbuka berasal dari build LAMA sementara
 * server (self-host Hostinger) hanya menyimpan aset build TERBARU pasca-deploy:
 * chunk JS/CSS ber-hash lama → 404 (`GET /_next/static/... 404` di log server)
 * → navigasi App Router melempar ChunkLoadError / gagal memuat CSS chunk →
 * pengguna melihat layar error dan harus reload manual.
 *
 * Solusi yang benar untuk pengguna = muat ulang halaman SEKALI (mengambil HTML
 * build terbaru yang merujuk aset yang memang ada). Guard ber-timestamp
 * (`shouldAutoReload`) mencegah loop reload bila error ternyata bukan karena
 * deploy. Dipakai `src/app/global-error.tsx`.
 */

/** Key sessionStorage penanda kapan auto-reload terakhir dilakukan. */
export const STALE_RELOAD_GUARD_KEY = "galea-stale-reload-at";

/** Jarak minimal antar auto-reload (anti loop reload tak berujung). */
export const STALE_RELOAD_WINDOW_MS = 60_000;

// Pola pesan error lintas browser/bundler saat chunk JS/CSS gagal dimuat.
const STALE_ASSET_PATTERNS: RegExp[] = [
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i
];

/** Apakah error ini bercorak "chunk/CSS build lama gagal dimuat"? */
export function isStaleAssetError(error: unknown): boolean {
  if (!error) return false;
  const parts: string[] = [];
  if (typeof error === "string") {
    parts.push(error);
  } else if (typeof error === "object") {
    const e = error as { name?: unknown; message?: unknown };
    if (typeof e.name === "string") parts.push(e.name);
    if (typeof e.message === "string") parts.push(e.message);
  }
  const text = parts.join(" ");
  if (!text) return false;
  return STALE_ASSET_PATTERNS.some((re) => re.test(text));
}

/** Parse nilai guard dari sessionStorage (string | null) → timestamp ms | null. */
export function parseReloadGuard(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Boleh auto-reload sekarang? True bila belum pernah reload atau reload terakhir
 * sudah lewat dari jendela guard (default 60 dtk).
 */
export function shouldAutoReload(
  lastReloadAt: number | null,
  now: number,
  windowMs: number = STALE_RELOAD_WINDOW_MS
): boolean {
  if (lastReloadAt == null) return true;
  return now - lastReloadAt > windowMs;
}
